import { useEffect, useMemo, useState } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
// local
import { nanoid } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import type { TAnyClientQuestionType, TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import CreateQuestionComponent from './CreateQuestionComponent';
// types
import { ECreateQuestionMode, QuestionEditorProvider } from './context/create-question.context';
import { lacksQuestionUpdatePermission } from './questionUpdatePermission';
import Loading from 'components/common/Loading';
import {
  useTemplateAccessGroupIds,
  useTemplateId,
  useTemplateLanguageSettings,
  useTemplateVisibility,
} from 'hooks/useTemplate';
import { useUser } from 'hooks/useUser';
import { useGetQuestionContextQuery } from 'rtk/query/apis/questions';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import type { RootState } from 'app/store';
import { removeQuestionEditor } from 'rtk/Question.Editor.Slice';
import { initializeQuestionEditor } from 'rtk/questionEditorThunks';
import { selectTemplateOptions } from 'rtk/TemplateSelector';
import { selectQuestionExists } from 'rtk/questionEditorSelector';
import { templateGroupsForNewQuestion } from 'utils/templateAccessGroups';
import { applyTemplateOptionsToQuestion } from 'utils/questionTemplateOptions';

const IN_TEMPLATE_MODES = [ECreateQuestionMode.InTemplateEdit, ECreateQuestionMode.InTemplateCopy];

interface Props extends ComponentProps<'div'> {
  mode?: ECreateQuestionMode;
  nodeId?: string | undefined;
  onClick?: () => void;
  saveProps?: {
    buttonText?: string;
    onClick?: (question: TAnyClientQuestionType) => void;
    onCreateQuestionFinished?: (question: TAnyClientQuestionType) => void;
  };
  saveWithBranchingProps?: {
    buttonText?: string;
    onClick?: (question: TAnyClientQuestionType) => void;
    onCreateQuestionFinished?: (question: TAnyClientQuestionType) => void;
  };
  onCancel?: () => void;
  language?: TAvailableLanguageCodes;
  clientId?: string;
}

const CreateQuestionContainerv2 = ({
  mode = ECreateQuestionMode.Create,
  nodeId,
  language,
  saveProps,
  saveWithBranchingProps,
  onCancel,
  clientId,
  className,
}: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const user = useUser();
  const templateId = useTemplateId();
  const templateAccessGroupIds = useTemplateAccessGroupIds();
  const templateVisibility = useTemplateVisibility();
  const { starting_language: templateStartingLanguage } = useTemplateLanguageSettings(templateId);
  const [questionId, setQuestionId] = useState<string | undefined>(undefined);
  const questionExistsInRedux = useAppSelector((state: RootState) =>
    questionId ? selectQuestionExists(state, questionId) : false,
  );
  const [inTemplateEditAndUneditable, setInTemplateEditAndUneditable] = useState<boolean | undefined>(undefined);
  // Save / CopyAndSwap / CopyAndSwapWithBranching all act on the same editor state and are mutually
  // exclusive — once one is in flight, disable the others so a second click can't fire a second
  // create off the same not-yet-refreshed question id.
  const [busyAction, setBusyAction] = useState<'save' | 'swap' | 'swapBranching' | null>(null);
  const templateOptions = useAppSelector((state: RootState) =>
    nodeId ? selectTemplateOptions(state, nodeId) : undefined,
  );

  const { data, isLoading, error } = useGetQuestionContextQuery(
    { questionId: nodeId, templateId },
    { skip: !nodeId, refetchOnMountOrArgChange: true },
  );

  const generatedId = useMemo(() => nanoid(), []);
  const {
    templateLanguage,
    questionLanguage,
    questionV4: questionConfig,
    preActions,
    postActions,
    intros,
    texts,
    outros,
    clarifies,
    publishedOrNonExclusive,
    hasQuestionUpdatePermission,
  } = data ?? {};

  useEffect(() => {
    if (isLoading) return;
    const editorLanguage = language ?? questionLanguage;
    dispatch(
      initializeQuestionEditor({
        nodeId,
        mode,
        // The same merge useQuestion gives the template's preview, so the editor lists what the
        // template actually runs: an option can exist only in the template's override (one added
        // to a shared question from the agent chat), and an option can have no override at all.
        // Only inside a template — elsewhere the Template slice may still hold another template's
        // parameters.
        questionConfig:
          questionConfig && editorLanguage && IN_TEMPLATE_MODES.includes(mode)
            ? applyTemplateOptionsToQuestion(questionConfig, templateOptions, editorLanguage)
            : questionConfig,
        questionVersion: questionConfig?.version ?? 4,
        language: editorLanguage,
        templateLanguage: templateLanguage ?? templateStartingLanguage,
        user,
        clientId,
        intros,
        texts,
        outros,
        clarifies,
        options: templateOptions,
        pre_actions: preActions ?? { value: [], isDefault: true },
        post_actions: postActions ?? { value: [], isDefault: true },
        isPublishedOrNonExclusiveQuestion: publishedOrNonExclusive ?? false,
        // Fail open (true) when unknown — the real write endpoint stays permission-gated regardless,
        // this only controls whether Save is pre-emptively disabled.
        hasQuestionUpdatePermission: hasQuestionUpdatePermission ?? true,
        inTemplateEditAndUneditable:
          mode === ECreateQuestionMode.InTemplateEdit
            ? (publishedOrNonExclusive ?? true) || !(hasQuestionUpdatePermission ?? true)
            : false,
        generatedId,
        // Only used when creating a brand-new question in a template; ignored for copies and edits.
        templateVisibilitySettings: {
          visibility: templateVisibility ?? null,
          accessGroupIds: templateGroupsForNewQuestion(user, templateAccessGroupIds),
        },
      }),
    ).then((result) => {
      if (initializeQuestionEditor.fulfilled.match(result)) {
        setQuestionId(result.payload?.questionId);
        setInTemplateEditAndUneditable(result.payload?.inTemplateEditAndUneditable);
      }
    });
  }, [isLoading, data, nodeId, dispatch, generatedId, language]);

  useEffect(() => {
    return () => {
      if (questionId) {
        dispatch(removeQuestionEditor(questionId));
      }
    };
  }, [questionId, dispatch]);

  if (isLoading || !questionId || !questionExistsInRedux) {
    return <Loading label="" />;
  }
  if (error) {
    toast.error(t('builder.toast.somethingWentWrong', { error: String(error) }));
  }

  return (
    <QuestionEditorProvider questionId={questionId}>
      <CreateQuestionComponent className={className}>
        <div
          className={clsx(
            'flex w-full flex-row justify-end gap-2',
            ![ECreateQuestionMode.InTemplateCreate, ECreateQuestionMode.InTemplateCopy].includes(mode) && 'p-4',
          )}
        >
          {mode !== ECreateQuestionMode.InTemplateEdit && (
            <CreateQuestionComponent.Close variant={'secondary'} onCancel={onCancel} />
          )}

          <CreateQuestionComponent.Save
            buttonText={saveProps?.buttonText}
            onClick={saveProps?.onClick}
            onCreateQuestionFinished={saveProps?.onCreateQuestionFinished}
            disabled={
              inTemplateEditAndUneditable ||
              lacksQuestionUpdatePermission(mode, hasQuestionUpdatePermission) ||
              (busyAction !== null && busyAction !== 'save')
            }
            originalQuestion={questionConfig}
            onBusyChange={(busy) => setBusyAction((prev) => (busy ? 'save' : prev === 'save' ? null : prev))}
          />

          {mode === ECreateQuestionMode.InTemplateCopy && saveWithBranchingProps ? (
            <>
              <CreateQuestionComponent.CopyAndSwapQuestion
                buttonText={t('builder.copyAndSwap')}
                onClick={saveWithBranchingProps?.onClick}
                onCreateQuestionFinished={saveWithBranchingProps?.onCreateQuestionFinished}
                originalQuestion={questionConfig}
                copyBranching={false}
                disabled={busyAction !== null && busyAction !== 'swap'}
                onBusyChange={(busy) => setBusyAction((prev) => (busy ? 'swap' : prev === 'swap' ? null : prev))}
              />

              <CreateQuestionComponent.CopyAndSwapQuestion
                buttonText={t('builder.copyAndSwapWithBranching')}
                onClick={saveWithBranchingProps?.onClick}
                onCreateQuestionFinished={saveWithBranchingProps?.onCreateQuestionFinished}
                originalQuestion={questionConfig}
                copyBranching={true}
                disabled={busyAction !== null && busyAction !== 'swapBranching'}
                onBusyChange={(busy) =>
                  setBusyAction((prev) => (busy ? 'swapBranching' : prev === 'swapBranching' ? null : prev))
                }
              />
            </>
          ) : null}
        </div>
      </CreateQuestionComponent>
    </QuestionEditorProvider>
  );
};

export default CreateQuestionContainerv2;
